import { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";

import { useOrderContext } from "contexts/OrderContext";
import ContentCard from "Components/shared/ContentCard";
import { useRTL } from "hooks/isRTL";
import LoadingBanner from "Components/shared/LoadingBanner";
import formatPhoneNumber from "utils/formatPhoneNumber";
import { fallbackValue } from "constants/fallbackConstant";

/**
 * Displays detailed information about the order, including shipment info,
 * fees, pickup and dropoff locations, and notes.
 */
const OrderDetailsTab = () => {
  const { orderDetails, isLoading, isError } = useOrderContext();
  const [sections, setSections] = useState([]);
  const { t } = useRTL();
  const userLanguage = localStorage.getItem("language") || "en";

  const formatField = (value) => {
    if (typeof value === "string" && value.startsWith("00962_")) {
    return formatPhoneNumber(value);
  }
    if (!value) return fallbackValue;
    if (typeof value === "object" && value.nameEn && value.nameAr) {
      return userLanguage === "ar" ? value.nameAr : value.nameEn;
    }
    if (typeof value === "object" && value.latitude && value.longitude) {
      return `https://maps.google.com/?q=${value.latitude},${value.longitude}`;
    }
    return value;
  };

  /**
   *  Converts location data into a display-friendly format.
   */
  const formatLocationData = (location) => {
    if (!location) return [];
    return Object.entries(location)
      .filter(([key]) => key !== "id")
      .map(([key, value]) => ({
        label: t(key),
        value: formatField(value),
      }));
  };
  const formatDropOffData = (location) => {
    if (!location) return [];
    return Object.entries(location)
      .filter(([key]) => (key !== "id" && key !== "buildingNumber" && key !== "floorNumber" && key !== "officeNumber") )
      .map(([key, value]) => ({
        label: t(key),
        value: formatField(value),
      }));
  };

  useEffect(() => {
    if (orderDetails) {
      const formattedSections = [
        {
          title: t("shipmentInfo"),
          content: [
            {
              label: t("quantity"),
              value: formatField(orderDetails.quantity),
            },
            {
              label: t("category"),
              value: formatField(orderDetails.category),
            },
            {
              label: t("preferedVehicleType"),
              value: formatField(orderDetails.preferredVehicleType),
            },
            {
              label: t("size"),
              value: formatField(orderDetails.size),
            },
            {
              label: t("contactPhoneNumber"),
              value: formatField(orderDetails.contactPhoneNumber),
            },
            {
              label: t("qrCodeId"),
              value: formatField(orderDetails.qrCodeId),
            },
          ],
        },
        {
          title: t("fees"),
          content: [
            {
              label: t("orderFees"),
              value: `${orderDetails.orderFees || 0} JD`,
            },
            {
              label: t("deliveryFees"),
              value: `${orderDetails.deliveryFees || 0} JD (${
                orderDetails.isPaid ? "Paid" : "Not Paid"
              })`,
            },
          ],
        },
        {
          title: t("pickUpLocation"),
          content: formatLocationData(orderDetails.pickUpLocation),
        },
        {
          title: t("dropOffLocation"),
          content: formatDropOffData(orderDetails.dropOffLocation),
        },
        {
          title: t("notes"),
          content: [
            {
              label: t("notes"),
              value: formatField(orderDetails.notes),
            },
          ],
        },
      ];

      setSections(formattedSections);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDetails, t]);

  if (isLoading) return <LoadingBanner />;
  if (isError) return <Typography>Error fetching order details</Typography>;

  const firstRow = sections.slice(0, 3);
  const secondRow = sections.slice(3);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "2rem",
        padding: "30px",
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: "2rem",
        }}
      >
        {firstRow.map((section, index) => (
          <ContentCard
            key={index}
            title={section.title}
            content={section.content}
          />
        ))}
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: "2.5rem",
        }}
      >
        {secondRow.map((section, index) => (
          <ContentCard
            key={index}
            title={section.title}
            content={section.content}
          />
        ))}
      </Box>
    </Box>
  );
};

export default OrderDetailsTab;
